import React, { useState } from 'react';
import { BRAND } from '../../styles/brand';
import {
  DemographicProfile, AdditionalDemographics, BedrijvenData, VoorzieningenData, BedrijvenOverpassData,
  OrganisatiesData, Gebiedsprofiel, InvloedsgebiedAnalyse, RaakvlakAnalyse, OmgevingsData,
} from '../../types';

interface GeraadpleegdeBronnenProps {
  buurtNaam?: string | null;
  demographics?: DemographicProfile | null;
  additionalDemographics?: AdditionalDemographics | null;
  bedrijven?: BedrijvenData | null;
  voorzieningen?: VoorzieningenData | null;
  bedrijvenOverpass?: BedrijvenOverpassData | null;
  organisaties?: OrganisatiesData | null;
  gebiedsprofiel?: Gebiedsprofiel | null;
  invloedsgebiedAnalyse?: InvloedsgebiedAnalyse | null;
  raakvlakAnalyse?: RaakvlakAnalyse | null;
  omgevingsData?: OmgevingsData | null;
}

interface Bron { naam: string; url: string; gebruiktVoor: string[] }

// De backend valt bij een onbereikbare of niet-dekkende API terug op voorbeelddata (id 'demo-…').
// Die telt niet als geraadpleegde bron.
const isDemo = (item: { id?: string | number }) => String(item?.id || '').startsWith('demo-');
const heeftEchteData = (items?: { id?: string | number }[]) => (items || []).some((i) => !isDemo(i));
const heeftDemoData = (items?: { id?: string | number }[]) => (items || []).some(isDemo);

/** Overzicht van de open databronnen die voor dít gebied daadwerkelijk gegevens hebben opgeleverd. */
export const GeraadpleegdeBronnen: React.FC<GeraadpleegdeBronnenProps> = (p) => {
  const [open, setOpen] = useState(false);
  const od = p.omgevingsData;
  const raakvlak = p.raakvlakAnalyse?.projecten || [];

  const kandidaten: Bron[] = [
    { naam: 'PDOK Locatieserver', url: 'https://www.pdok.nl/introductie/-/article/pdok-locatieserver', gebruiktVoor: [p.buurtNaam ? 'buurt en adressen' : ''] },
    {
      naam: 'CBS StatLine — Kerncijfers wijken en buurten', url: 'https://opendata.cbs.nl/statline',
      gebruiktVoor: [
        p.demographics ? 'inwoners, leeftijd, woningvoorraad en inkomen' : '',
        p.additionalDemographics ? 'huishoudens, WOZ-waarde en herkomst' : '',
      ],
    },
    {
      naam: 'Kadaster — Basisregistratie Adressen en Gebouwen (BAG)', url: 'https://www.kadaster.nl/zakelijk/registraties/basisregistraties/bag',
      gebruiktVoor: [
        (p.bedrijven?.totaal || 0) > 0 ? 'bedrijfspanden' : '',
        (p.invloedsgebiedAnalyse?.totaalPandenInGebied || 0) > 0 ? 'woningen in het invloedsgebied' : '',
      ],
    },
    {
      naam: 'OpenStreetMap', url: 'https://www.openstreetmap.org/copyright',
      gebruiktVoor: [
        (p.voorzieningen?.items?.length || 0) > 0 ? 'voorzieningen' : '',
        (p.bedrijvenOverpass?.items?.length || 0) > 0 ? 'bedrijven' : '',
        (p.organisaties?.items?.length || 0) > 0 ? 'maatschappelijke organisaties' : '',
      ],
    },
    {
      naam: 'NDW — Nationale Databank Wegverkeersgegevens', url: 'https://opendata.ndw.nu',
      gebruiktVoor: [
        heeftEchteData(od?.werkzaamheden) ? 'wegwerkzaamheden' : '',
        raakvlak.some((r) => r.bronType === 'werkzaamheden') ? 'raakvlakprojecten' : '',
      ],
    },
    {
      naam: 'Officiële bekendmakingen', url: 'https://zoek.officielebekendmakingen.nl',
      gebruiktVoor: [
        (od?.vergunningen?.length || 0) > 0 ? 'vergunningen' : '',
        raakvlak.some((r) => r.bronType === 'vergunning') ? 'raakvlakprojecten' : '',
      ],
    },
    { naam: 'Gemeente Amsterdam — Signalen', url: 'https://api.data.amsterdam.nl', gebruiktVoor: [heeftEchteData(od?.meldingen) ? 'meldingen openbare ruimte' : ''] },
  ];
  const bronnen = kandidaten
    .map((b) => ({ ...b, gebruiktVoor: b.gebruiktVoor.filter(Boolean) }))
    .filter((b) => b.gebruiktVoor.length > 0);

  const voorbeeld = [
    heeftDemoData(od?.meldingen) ? 'meldingen' : '',
    heeftDemoData(od?.werkzaamheden) ? 'wegwerkzaamheden' : '',
  ].filter(Boolean);

  if (bronnen.length === 0) return null;

  return (
    <div style={{ marginTop: 16, border: `1px solid ${BRAND.rand}`, borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 14px', border: 'none', background: open ? BRAND.papier : '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={BRAND.groenMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: BRAND.groen }}>Geraadpleegde bronnen</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: BRAND.groenMuted }}>{bronnen.length}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={BRAND.groenMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <div style={{ padding: '4px 14px 14px' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {bronnen.map((b, i) => (
              <li key={b.naam} style={{ padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid #f0f1f4' }}>
                <a href={b.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: BRAND.groen, textDecoration: 'underline', textUnderlineOffset: 2 }}>{b.naam}</a>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Gebruikt voor: {b.gebruiktVoor.join(', ')}</div>
              </li>
            ))}
          </ul>
          {p.gebiedsprofiel?.narrative && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>Het gebiedsprofiel is een AI-samenvatting van deze gegevens.</p>
          )}
          {voorbeeld.length > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
              Voor {voorbeeld.join(', ')} was voor dit gebied geen open data beschikbaar; wat daarvan op de kaart staat zijn voorbeeldgegevens.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
